import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { Offcanvas } from "react-bootstrap";
import { FaSearch, FaUsers } from "react-icons/fa";
import { MdVisibility, MdEmail, MdPhone, MdLocationOn } from "react-icons/md";
import { IoIosArrowForward } from "react-icons/io";

import {
  getAlmataPartners,
  getOemPartners,
  getOemPartnerUsers,
} from "../../redux/actions/almataActions";

import Pagination from "../../../helper/Pagination";
import "../../styles/InputSheet.css";

const AlmataSplitView = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const initialPartner = location.state?.partner;
  const initialPageNumber = location.state?.partnerPageNumber || 1;
  const initialStatus = location.state?.partnerStatus || "active";
  const { user } = useSelector((state) => state.auth || {});
  const {
    partners = [],
    loadingPartners = false,
    pagination = {},
    oemPartners = [],
    loadingOemPartners = false,
    oemPagination = {},
    oemUsers = [],
    loadingOemUsers = false,
    oemUsersPagination = {},
  } = useSelector((state) => state.almata || {});

  const adminId = user?.id || user?._id;

  // Left panel state
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState(initialStatus);
  const [currentPage, setCurrentPage] = useState(initialPageNumber);
  const [almataAccordionOpen, setAlmataAccordionOpen] = useState(true); // Default open

  // Right panel state
  const [selectedPartner, setSelectedPartner] = useState(
    initialPartner || null,
  );
  const [oemSearchTerm, setOemSearchTerm] = useState("");
  const [oemStatusFilter, setOemStatusFilter] = useState("active");
  const [oemCurrentPage, setOemCurrentPage] = useState(1);

  // End users state
  const [selectedOemPartner, setSelectedOemPartner] = useState(null);
  const [endUserSearchTerm, setEndUserSearchTerm] = useState("");
  const [endUserCurrentPage, setEndUserCurrentPage] = useState(1);
  const [accordionOpen, setAccordionOpen] = useState(true);
  const [openAccordions, setOpenAccordions] = useState({});

  // Offcanvas state
  const [endUserOffcanvas, setEndUserOffcanvas] = useState({
    isOpen: false,
    user: null,
  });

  // Plan details modal state
  const [planDetailsModal, setPlanDetailsModal] = useState({
    isOpen: false,
    plans: null,
    position: { top: 0, left: 0 },
  });

  // Load Almata Partners
  useEffect(() => {
    if (adminId) {
      dispatch(
        getAlmataPartners({
          limit: 10,
          page: currentPage,
          search: searchTerm,
          status: statusFilter,
          createdBy: adminId,
        }),
      );
    }
  }, [dispatch, currentPage, searchTerm, statusFilter, adminId]);

  // Load OEM Partners when Almata Partner is selected
  useEffect(() => {
    if (selectedPartner?._id) {
      dispatch(
        getOemPartners({
          limit: 10,
          page: oemCurrentPage,
          search: oemSearchTerm,
          status: oemStatusFilter,
          createdBy: selectedPartner._id,
        }),
      );
    }
  }, [
    dispatch,
    selectedPartner,
    oemCurrentPage,
    oemSearchTerm,
    oemStatusFilter,
  ]);

  // Auto-select first OEM partner and load end users when OEM partners are loaded
  useEffect(() => {
    if (oemPartners.length > 0 && !selectedOemPartner) {
      const firstOemPartner = oemPartners[0];
      setSelectedOemPartner(firstOemPartner);
    } else if (oemPartners.length === 0 && selectedOemPartner) {
      // Clear selected OEM partner if no OEM partners are available
      setSelectedOemPartner(null);
    }
  }, [oemPartners, selectedOemPartner]);

  // Load End Users when OEM Partner is selected
  useEffect(() => {
    if (selectedOemPartner?._id) {
      dispatch(
        getOemPartnerUsers({
          oemId: selectedOemPartner._id,
          page: endUserCurrentPage,
          limit: 10,
          search: endUserSearchTerm,
          status: "active",
        }),
      );
    }
  }, [dispatch, selectedOemPartner, endUserCurrentPage, endUserSearchTerm]);

  const handlePartnerClick = (partner) => {
    setSelectedPartner(partner);
    setSelectedOemPartner(null); // Reset OEM selection to trigger auto-selection
    setOemCurrentPage(1);
    setOemSearchTerm("");
    setEndUserCurrentPage(1);
    setEndUserSearchTerm("");
    // Clear any existing OEM users data
    // This will be handled by the useEffect when selectedOemPartner becomes null
  };

  const handleOemPartnerClick = (oemPartner) => {
    setSelectedOemPartner(oemPartner);
    setEndUserCurrentPage(1);
    setEndUserSearchTerm("");
  };

  const handleEndUserClick = (user) => {
    setEndUserOffcanvas({ isOpen: true, user });
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  return (
    <div className="page-container">
      <div className="page-card">
        <div className="form-header">
          <div>
            <button
              className="cancel-btn-deleteModal ms-2"
              onClick={() => window.history.back()}
            >
              Back
            </button>
            <span className="ms-2 fw-bold">Almata Partners Management</span>
          </div>
        </div>

        <div className="row g-3 mx-auto p-2">
          <div className="col-md-4 px-0">
            <div
              className=" rounded-0 "
              style={{
                height: almataAccordionOpen ? "20rem" : "auto",
                backgroundColor: "#f9fafb",
              }}
            >
              <div className="d-flex flex-column h-100">
                <div className="mt-3 mb-3 px-2">
                  <div className="row align-items-center justify-content-center">
                    <div div className="col-4">
                      <button
                        className="btn btn-link p-0 text-decoration-none d-flex align-items-center"
                        onClick={() =>
                          setAlmataAccordionOpen(!almataAccordionOpen)
                        }
                        style={{ border: "none", background: "none" }}
                      >
                        <h6 className="mb-0 fw-bold text-primary me-2">
                          Almata Partners
                        </h6>
                        <IoIosArrowForward
                          style={{
                            transition: "transform 0.3s ease",
                            transform: almataAccordionOpen
                              ? "rotate(90deg)"
                              : "rotate(0deg)",
                            fontSize: "16px",
                            color: "#3b82f6",
                          }}
                        />
                      </button>{" "}
                    </div>
                    {/* Search and Filters */}
                    <div className="position-relative col-8">
                      <input
                        type="search"
                        placeholder="Search partners..."
                        className="form-input"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        style={{ width: "100%", fontSize: "14px" }}
                      />
                      {searchTerm === "" && (
                        <div
                          className="position-absolute"
                          style={{
                            top: "50%",
                            right: "20px",
                            color: "#666",
                            transform: "translateY(-50%)",
                          }}
                        >
                          <FaSearch />
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Partners List */}
                {almataAccordionOpen && (
                  <>
                    <div
                      className="flex-grow-1 overflow-auto scroll-container px-2"
                      style={{
                        height: "600px",
                        scrollbarWidth: "thin",
                        scrollbarColor: "#cbd5e1 #f1f5f9",
                      }}
                    >
                      <style>{`
                    .scroll-container::-webkit-scrollbar {
                      width: 6px;
                    }
                    .scroll-container::-webkit-scrollbar-track {
                      background: #f1f5f9;
                    }
                    .scroll-container::-webkit-scrollbar-thumb {
                      background: #cbd5e1;
                      border-radius: 3px;
                    }
                    .scroll-container::-webkit-scrollbar-thumb:hover {
                      background: #94a3b8;
                    }
                  `}</style>
                      {loadingPartners ? (
                        <div
                          className="d-flex flex-column align-items-center justify-content-center p-4"
                          style={{ minHeight: "200px" }}
                        >
                          <div
                            className="spinner-border text-primary mb-3"
                            role="status"
                          >
                            <span className="visually-hidden">Loading...</span>
                          </div>
                          <h6 className="text-muted mb-1">
                            Loading Almata Partners...
                          </h6>
                        </div>
                      ) : partners.length === 0 ? (
                        <div className="text-center p-3 text-muted">
                          No partners found
                        </div>
                      ) : (
                        <>
                          {partners.map((partner, index) => (
                            <div key={partner._id}>
                              <div
                                className="cursor-pointer py-2 px-2"
                                onClick={() => handlePartnerClick(partner)}
                                style={{
                                  transition: "all 0.2s ease",
                                  backgroundColor:
                                    selectedPartner?._id === partner._id
                                      ? "#e8f0fe"
                                      : "white",
                                  borderLeft:
                                    selectedPartner?._id === partner._id
                                      ? "3px solid #4285f4"
                                      : "3px solid transparent",
                                  borderBottom: "1px solid #e9ecef",
                                }}
                                onMouseEnter={(e) => {
                                  if (selectedPartner?._id !== partner._id) {
                                    e.currentTarget.style.backgroundColor =
                                      "#f8f9ff";
                                  }
                                }}
                                onMouseLeave={(e) => {
                                  if (selectedPartner?._id !== partner._id) {
                                    e.currentTarget.style.backgroundColor =
                                      "white";
                                  }
                                }}
                              >
                                <div>
                                  <div className="d-flex align-items-start">
                                    {/* <div className="me-2">
                                      {index + 1 + (currentPage - 1) * 10}.
                                    </div> */}

                                    {partner.logo && (
                                      <img
                                        src={partner.logo}
                                        alt={partner.companyName}
                                        className="me-2"
                                        style={{
                                          height: "30px",
                                          width: "30px",
                                          objectFit: "contain",
                                          borderRadius: "4px",
                                          border: "1px solid #ddd",
                                          backgroundColor: "#f1f3f5",
                                        }}
                                      />
                                    )}
                                    <div className="flex-grow-1">
                                      <h6
                                        className="mb-0"
                                        style={{ fontSize: "14px" }}
                                      >
                                        {partner.companyName}
                                      </h6>
                                      <small className="text-muted">
                                        {partner.email}
                                      </small>
                                    </div>
                                    <span
                                      className={`badge ${partner.status === "active" ? "bg-success" : "bg-secondary"}`}
                                      style={{ fontSize: "10px" }}
                                    >
                                      {partner.status?.toUpperCase()}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </>
                      )}
                    </div>

                    {/* Pagination */}
                    <div className="mt-2 px-2">
                      <Pagination
                        currentPage={currentPage}
                        totalPages={pagination.totalPages}
                        totalItems={pagination.totalItems}
                        itemsPerPage={pagination.itemsPerPage}
                        onPageChange={setCurrentPage}
                      />
                    </div>
                  </>
                )}
              </div>
            </div>
            <div
              className=" rounded-0 mt-2"
              style={{
                height: almataAccordionOpen ? "30rem" : "46rem",
                backgroundColor: "#f9fafb",
              }}
            >
              <div className="d-flex flex-column h-100">
                <div className="mt-3 mb-3 px-2">
                  <div className="row align-items-center">
                    <div className="col-6">
                      <h6 className="mb-0 fw-bold text-primary">
                        OEM Partners
                      </h6>
                    </div>
                    <div className="col-6">
                      <div className="position-relative">
                        <input
                          type="search"
                          placeholder="Search OEM..."
                          className="form-input"
                          value={oemSearchTerm}
                          onChange={(e) => setOemSearchTerm(e.target.value)}
                          style={{ width: "100%", fontSize: "14px" }}
                        />
                        {oemSearchTerm === "" && (
                          <div
                            className="position-absolute"
                            style={{
                              top: "50%",
                              right: "20px",
                              color: "#666",
                              transform: "translateY(-50%)",
                            }}
                          >
                            <FaSearch />
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                <div
                  className="flex-grow-1 overflow-auto scroll-container px-2"
                  style={{
                    scrollbarWidth: "thin",
                    scrollbarColor: "#cbd5e1 #f1f5f9",
                  }}
                >
                  <style>{`
                    .scroll-container::-webkit-scrollbar {
                      width: 6px;
                    }
                    .scroll-container::-webkit-scrollbar-track {
                      background: #f1f5f9;
                    }
                    .scroll-container::-webkit-scrollbar-thumb {
                      background: #cbd5e1;
                      border-radius: 3px;
                    }
                    .scroll-container::-webkit-scrollbar-thumb:hover {
                      background: #94a3b8;
                    }
                    .hover-bg-light:hover {
                      background-color: #f8f9fa !important;
                    }
                    .cursor-pointer {
                      cursor: pointer;
                    }
                    .eye-icon-hover {
                      color: black;
                      cursor: pointer;
                      transition: color 0.2s;
                    }
                    .eye-icon-hover:hover {
                      color: #3b82f6;
                    }
                  `}</style>
                  {loadingOemPartners ? (
                    <div className="text-center py-4">
                      <div
                        className="spinner-border text-primary"
                        role="status"
                      >
                        <span className="visually-hidden">Loading...</span>
                      </div>
                      <p className="mt-2 text-muted">Loading OEM Partners...</p>
                    </div>
                  ) : oemPartners.length === 0 ? (
                    <div className="text-center py-4 text-muted">
                      No OEM partners found
                    </div>
                  ) : (
                    oemPartners.map((oemPartner, index) => (
                      <div
                        key={oemPartner._id}
                        className={`p-3 border-bottom cursor-pointer hover-bg-light ${
                          selectedOemPartner?._id === oemPartner._id
                            ? "bg-primary bg-opacity-10 border-primary"
                            : ""
                        }`}
                        onClick={() => handleOemPartnerClick(oemPartner)}
                        style={{
                          borderLeft:
                            selectedOemPartner?._id === oemPartner._id
                              ? "3px solid #3b82f6"
                              : "3px solid transparent",
                        }}
                      >
                        <div className="d-flex align-items-center">
                          <div className="me-2 text-muted">
                            {index + 1 + (oemCurrentPage - 1) * 10}.
                          </div>
                          {oemPartner.logo && (
                            <img
                              src={oemPartner.logo}
                              alt="Logo"
                              className="rounded me-2"
                              style={{
                                width: "32px",
                                height: "32px",
                                objectFit: "cover",
                                backgroundColor: "#c5c7caff",
                              }}
                            />
                          )}
                          <div className="flex-grow-1">
                            <h6
                              className="mb-1 fw-bold"
                              style={{ fontSize: "14px" }}
                            >
                              {oemPartner.companyName}
                            </h6>
                            <small
                              className="text-muted"
                              style={{ fontSize: "12px" }}
                            >
                              {oemPartner.email}
                            </small>
                          </div>
                          <div className="d-flex flex-column align-items-end">
                            <span
                              className={`badge ${
                                oemPartner.status === "active"
                                  ? "bg-success"
                                  : "bg-secondary"
                              }`}
                              style={{ fontSize: "10px" }}
                            >
                              {oemPartner.status?.toUpperCase()}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>

                {oemPagination.totalPages > 1 && (
                  <div className="px-2 py-2 border-top">
                    <Pagination
                      currentPage={oemCurrentPage}
                      totalPages={oemPagination.totalPages}
                      totalItems={oemPagination.totalItems}
                      itemsPerPage={oemPagination.itemsPerPage}
                      onPageChange={setOemCurrentPage}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Right Panel */}
          <div className="col-md-8">
            <div
              className=" rounded-0  p-3"
              style={{
                height: almataAccordionOpen ? "50rem" : "50rem",
                backgroundColor: "#f9fafb",
                transition: "all 0.3s ease",
              }}
            >
              {!selectedPartner ? (
                <div className="d-flex align-items-center justify-content-center h-100">
                  <div className="text-center text-muted">
                    <h5>Select an Almata Partner</h5>
                    <p>
                      Choose a partner from the left panel to view their details
                    </p>
                  </div>
                </div>
              ) : !selectedOemPartner &&
                oemPartners.length === 0 &&
                !loadingOemPartners ? (
                <div className="d-flex align-items-center justify-content-center h-100">
                  <div className="text-center text-muted">
                    <div className="fs-1">
                      <FaUsers />
                    </div>
                    <h5>No OEM Partners Found</h5>
                    <p>This Almata Partner has no OEM partners associated</p>
                  </div>
                </div>
              ) : !selectedOemPartner && loadingOemPartners ? (
                <div className="d-flex align-items-center justify-content-center h-100">
                  <div className="text-center">
                    <div
                      className="spinner-border text-primary mb-3"
                      role="status"
                      style={{ width: "3rem", height: "3rem" }}
                    >
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    <h5 className="text-muted mb-2">Loading OEM Partners...</h5>
                  </div>
                </div>
              ) : selectedOemPartner && loadingOemUsers ? (
                <div className="d-flex align-items-center justify-content-center h-100">
                  <div className="text-center">
                    <div
                      className="spinner-border text-primary mb-3"
                      role="status"
                      style={{ width: "3rem", height: "3rem" }}
                    >
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    <h5 className="text-muted mb-2">Loading End Users...</h5>
                    <div className="mt-3">
                      <div
                        className="progress"
                        style={{
                          height: "4px",
                          width: "200px",
                          margin: "0 auto",
                        }}
                      >
                        <div
                          className="progress-bar progress-bar-striped progress-bar-animated"
                          role="progressbar"
                          style={{ width: "100%" }}
                        ></div>
                      </div>
                    </div>
                  </div>
                </div>
              ) : selectedOemPartner ? (
                // End Users View - Always show when OEM partner is selected
                <div className="d-flex flex-column h-100">
                  {/* OEM Partner Details */}
                  <div
                    className="mb-3  bg-white "
                    style={{ borderBottom: "1px solid #e9ecef" }}
                  >
                    <button
                      className="w-100 d-flex align-items-center justify-content-between p-0   border-0 px-3  py-2"
                      onClick={() => setAccordionOpen(!accordionOpen)}
                      style={{ cursor: "pointer" }}
                    >
                      <div className="d-flex align-items-center">
                        {selectedOemPartner?.logo && (
                          <img
                            src={selectedOemPartner.logo}
                            alt={selectedOemPartner.companyName}
                            className="me-2"
                            style={{
                              height: "20px",
                              width: "30px",
                              objectFit: "contain",
                              borderRadius: "4px",
                            }}
                          />
                        )}
                        <span className="fw-medium text-primary">
                          {selectedOemPartner?.companyName || "Loading..."}
                        </span>
                      </div>
                      <IoIosArrowForward
                        style={{
                          transition: "transform 0.2s",
                          transform: accordionOpen
                            ? "rotate(90deg)"
                            : "rotate(0deg)",
                          fontSize: "18px",
                        }}
                      />
                    </button>
                    {accordionOpen && (
                      <div
                        className="p-2"
                        style={{ animation: "fadeIn 0.2s ease" }}
                      >
                        <div className="row mt-1 px-2 g-3 ">
                          <div className="col-md-6">
                            <div className="d-flex align-items-center mb-2">
                              <small className="mb-0 fw-bold text-muted">
                                Email :{" "}
                              </small>
                              <span className="small ms-1">
                                {selectedOemPartner?.email || "N/A"}
                              </span>
                            </div>
                            <div className="d-flex align-items-center mb-2">
                              <small className="mb-0 fw-bold text-muted">
                                Phone :
                              </small>
                              <span className="small ms-1">
                                {selectedOemPartner?.countryCode || ""}{" "}
                                {selectedOemPartner?.contactNumber || "N/A"}
                              </span>
                            </div>
                            <div className="d-flex align-items-center">
                              <small className="mb-0 fw-bold text-muted">
                                Country :
                              </small>
                              <span className="small ms-1">
                                {selectedOemPartner?.country || "N/A"} (
                                {selectedOemPartner?.currency || "N/A"})
                              </span>
                            </div>
                          </div>

                          <div className="col-md-6">
                            <div className="mb-2">
                              <small className="mb-0 fw-bold text-muted">
                                Contact Person :
                              </small>
                              <span className="small ms-1">
                                {selectedOemPartner?.firstName || ""}{" "}
                                {selectedOemPartner?.lastName || ""}
                              </span>
                            </div>
                            <div className="mb-2">
                              <small className="mb-0 fw-bold text-muted">
                                Plans details :
                              </small>
                              <span className="small ms-1">
                                {selectedOemPartner?.planDetails &&
                                selectedOemPartner.planDetails.length > 0 ? (
                                  <>
                                    {selectedOemPartner.planDetails.length}{" "}
                                    Plan(s)
                                    <MdVisibility
                                      className="ms-1 eye-icon-hover"
                                      style={{ fontSize: "14px" }}
                                      onClick={(e) => {
                                        const rect =
                                          e.target.getBoundingClientRect();
                                        setPlanDetailsModal({
                                          isOpen: true,
                                          plans: selectedOemPartner.planDetails,
                                          position: {
                                            top: rect.bottom + 5,
                                            left: rect.left,
                                          },
                                        });
                                      }}
                                      title="View Plan Details"
                                    />
                                  </>
                                ) : (
                                  "N/A"
                                )}
                              </span>
                            </div>
                            <div className="mb-2">
                              <small className="mb-0 fw-bold text-muted">
                                Scan Type:
                              </small>
                              <span className="small ms-1">
                                {selectedOemPartner?.scanType?.name || "N/A"}
                              </span>
                            </div>
                          </div>
                        </div>

                        {selectedOemPartner?.almataSharedDiscription && (
                          <div className="mt-2 p-2 bg-light rounded">
                            <small className="text-muted mb-0 fw-bold">
                              Notes :
                            </small>
                            <span className="small text-wrap ms-1">
                              {selectedOemPartner.almataSharedDiscription}
                            </span>
                          </div>
                        )}
                      </div>
                    )}
                    <style>{`
                      @keyframes fadeIn {
                        from {
                          opacity: 0;
                          transform: translateY(-10px);
                        }
                        to {
                          opacity: 1;
                          transform: translateY(0);
                        }
                      }
                    `}</style>
                  </div>
                  {/* Search Bar */}
                  <div className="mb-3">
                    <div className="d-flex align-items-center justify-content-between">
                      <h6 className="mb-0">End Users</h6>
                      <div className="position-relative">
                        <input
                          type="search"
                          placeholder="Search end users..."
                          className="form-input"
                          value={endUserSearchTerm}
                          onChange={(e) => setEndUserSearchTerm(e.target.value)}
                          style={{ width: "200px", fontSize: "14px" }}
                        />
                        {endUserSearchTerm === "" && (
                          <div
                            className="position-absolute"
                            style={{
                              top: "50%",
                              right: "13px",
                              color: "#666",
                              transform: "translateY(-50%)",
                            }}
                          >
                            <FaSearch />
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  <div
                    className="flex-grow-1 overflow-auto"
                    style={{
                      maxHeight: "400px",
                      scrollbarWidth: "thin",
                      scrollbarColor: "#cbd5e1 #f1f5f9",
                    }}
                  >
                    <style>{`
                      .table-scroll-wrapper::-webkit-scrollbar {
                        width: 6px;
                      }
                      .table-scroll-wrapper::-webkit-scrollbar-track {
                        background: #f1f5f9;
                      }
                      .table-scroll-wrapper::-webkit-scrollbar-thumb {
                        background: #cbd5e1;
                        border-radius: 3px;
                      }
                      .table-scroll-wrapper::-webkit-scrollbar-thumb:hover {
                        background: #94a3b8;
                      }
                    `}</style>
                    <div className="table-scroll-wrapper">
                      <table className="partners-table">
                        <thead>
                          <tr>
                            <th>Sr No.</th>
                            <th>Name</th>
                            <th>Email</th>
                            <th>Domain</th>
                            <th>Verified</th>
                            <th>Status</th>
                            <th>Created</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {loadingOemUsers ? (
                            <tr>
                              <td colSpan="8" className="text-center p-5">
                                <div className="d-flex flex-column align-items-center justify-content-center">
                                  <div
                                    className="spinner-border text-primary mb-3"
                                    role="status"
                                  >
                                    <span className="visually-hidden">
                                      Loading...
                                    </span>
                                  </div>
                                  <h6 className="text-muted mb-1">
                                    Loading End Users
                                  </h6>
                                  <small className="text-muted">
                                    Retrieving user information...
                                  </small>
                                </div>
                              </td>
                            </tr>
                          ) : oemUsers.length === 0 ? (
                            <tr>
                              <td
                                colSpan="8"
                                className="text-center p-3 text-muted"
                              >
                                No end users found
                              </td>
                            </tr>
                          ) : (
                            oemUsers
                              .filter((user) => user && user.email)
                              .map((user, index) => (
                                <tr key={user._id || index}>
                                  <td>
                                    {(endUserCurrentPage - 1) * 10 + index + 1}
                                  </td>
                                  <td>
                                    {user.firstName && user.lastName
                                      ? `${user.firstName} ${user.lastName}`
                                      : user.firstName ||
                                        user.lastName ||
                                        "N/A"}
                                  </td>
                                  <td>{user.email || "N/A"}</td>
                                  <td>{user.domain || "N/A"}</td>
                                  <td>
                                    <span
                                      className={`badge ${user.verified ? "bg-success" : "bg-warning"}`}
                                    >
                                      {user.verified ? "Yes" : "No"}
                                    </span>
                                  </td>
                                  <td>
                                    <span
                                      className={`badge ${user.status === "active" ? "bg-success" : "bg-secondary"}`}
                                    >
                                      {user.status?.toUpperCase()}
                                    </span>
                                  </td>
                                  <td>{formatDate(user.createdAt)}</td>
                                  <td>
                                    <MdVisibility
                                      className="cursor-pointer view-row-btn"
                                      onClick={() => handleEndUserClick(user)}
                                      title="View Details"
                                      style={{
                                        color: "#666",
                                        cursor: "pointer",
                                      }}
                                    />
                                  </td>
                                </tr>
                              ))
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* End Users Pagination */}
                  <div className="mt-2 px-2">
                    <Pagination
                      currentPage={endUserCurrentPage}
                      totalPages={oemUsersPagination.totalPages}
                      totalItems={oemUsersPagination.totalItems}
                      itemsPerPage={oemUsersPagination.itemsPerPage}
                      onPageChange={setEndUserCurrentPage}
                    />
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </div>

      {/* End User Details Offcanvas */}
      <Offcanvas
        show={endUserOffcanvas.isOpen}
        onHide={() => setEndUserOffcanvas({ isOpen: false, user: null })}
        placement="end"
        style={{ width: "450px" }}
      >
        <Offcanvas.Header className="text-black border-bottom px-3 py-2">
          <Offcanvas.Title className="d-flex align-items-center">
            End User Profile
          </Offcanvas.Title>
          <button
            type="button"
            className="btn-close text-black"
            onClick={() => setEndUserOffcanvas({ isOpen: false, user: null })}
            aria-label="Close"
          />
        </Offcanvas.Header>

        <Offcanvas.Body className="p-0">
          {endUserOffcanvas.user && (
            <>
              {/* Profile Header */}
              <div className="bg-gradient-primary text-black p-2">
                <div className="d-flex align-items-center">
                  <div
                    className="rounded-circle d-flex align-items-center justify-content-center me-2"
                    style={{
                      width: "45px",
                      height: "45px",
                      fontSize: "18px",
                      fontWeight: "bold",
                      backgroundColor: "rgb(197, 199, 202)",
                    }}
                  >
                    {endUserOffcanvas.user.firstName
                      ?.charAt(0)
                      ?.toUpperCase() ||
                      endUserOffcanvas.user.email?.charAt(0)?.toUpperCase() ||
                      "U"}
                  </div>
                  <div className="flex-grow-1">
                    <h6 className="mb-1 text-black">
                      {endUserOffcanvas.user.firstName &&
                      endUserOffcanvas.user.lastName
                        ? `${endUserOffcanvas.user.firstName} ${endUserOffcanvas.user.lastName}`
                        : endUserOffcanvas.user.firstName ||
                          endUserOffcanvas.user.lastName ||
                          "Unknown User"}
                    </h6>
                    <div className="d-flex gap-1">
                      <span
                        className={`badge badge-sm ${endUserOffcanvas.user.status === "active" ? "bg-success" : "bg-secondary"}`}
                      >
                        {endUserOffcanvas.user.status?.toUpperCase()}
                      </span>
                      <span
                        className={`badge badge-sm ${endUserOffcanvas.user.verified ? "bg-success" : "bg-warning"}`}
                      >
                        {endUserOffcanvas.user.verified
                          ? "Verified"
                          : "Unverified"}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Contact Information */}
              <div className="p-2 border-bottom">
                <h6 className="text-primary mb-2 d-flex align-items-center small">
                  <MdEmail className="me-1" size={16} /> Contact Information
                </h6>
                <div className="row g-2">
                  <div className="col-12">
                    <div className="d-flex align-items-center p-2 bg-light rounded">
                      <MdEmail
                        className="text-muted me-2"
                        style={{ fontSize: "16px" }}
                      />
                      <div>
                        <small className="text-muted d-block">
                          Email Address
                        </small>
                        <span className="small fw-medium">
                          {endUserOffcanvas.user.email}
                        </span>
                      </div>
                    </div>
                  </div>
                  {endUserOffcanvas.user.domain && (
                    <div className="col-12">
                      <div className="d-flex align-items-center p-2 bg-light rounded">
                        <span
                          className="text-muted me-2"
                          style={{ fontSize: "16px" }}
                        >
                          🌐
                        </span>
                        <div>
                          <small className="text-muted d-block">Domain</small>
                          <span className="small fw-medium">
                            {endUserOffcanvas.user.domain}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Account Details */}
              <div className="p-2 border-bottom">
                <h6 className="text-primary mb-2 small">Account Details</h6>
                <div className="row g-2">
                  <div className="col-6">
                    <div className="text-center p-2 bg-light rounded">
                      <div className="h6 mb-1 text-primary">
                        {endUserOffcanvas.user.language?.toUpperCase() || "ENG"}
                      </div>
                      <small className="text-muted">Language</small>
                    </div>
                  </div>
                  <div className="col-6">
                    <div className="text-center p-2 bg-light rounded">
                      <div className="h6 mb-1 text-primary">
                        {endUserOffcanvas.user.flag || 0}
                      </div>
                      <small className="text-muted">Flag Status</small>
                    </div>
                  </div>
                  <div className="col-6">
                    <div className="text-center p-2 bg-light rounded">
                      <div className="h6 mb-1 text-primary">
                        {endUserOffcanvas.user.policy ? "✓" : "✗"}
                      </div>
                      <small className="text-muted">Policy Accepted</small>
                    </div>
                  </div>
                  <div className="col-6">
                    <div className="text-center p-2 bg-light rounded">
                      <div className="h6 mb-1 text-primary">
                        {endUserOffcanvas.user.initiatedPayment ? "✓" : "✗"}
                      </div>
                      <small className="text-muted">Payment Initiated</small>
                    </div>
                  </div>
                </div>
              </div>
              {/* Timeline */}
              <div className="p-2">
                <h6 className="text-primary mb-2 small">Timeline</h6>
                <div className="timeline">
                  <div className="d-flex align-items-start mb-2">
                    <div
                      className="bg-success rounded-circle me-2 mt-1"
                      style={{ width: "6px", height: "6px" }}
                    ></div>
                    <div className="flex-grow-1">
                      <div className="d-flex justify-content-between align-items-center">
                        <span className="fw-medium small">Account Created</span>
                        <small className="text-muted">
                          {new Date(
                            endUserOffcanvas.user.createdAt,
                          ).toLocaleDateString("en-US", {
                            year: "numeric",
                            month: "short",
                            day: "numeric",
                          })}
                        </small>
                      </div>
                      <small className="text-muted">
                        {new Date(
                          endUserOffcanvas.user.createdAt,
                        ).toLocaleTimeString("en-US", {
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </small>
                    </div>
                  </div>
                  {endUserOffcanvas.user.updatedAt && (
                    <div className="d-flex align-items-start">
                      <div
                        className="bg-primary rounded-circle me-2 mt-1"
                        style={{ width: "6px", height: "6px" }}
                      ></div>
                      <div className="flex-grow-1">
                        <div className="d-flex justify-content-between align-items-center">
                          <span className="fw-medium small">Last Updated</span>
                          <small className="text-muted">
                            {new Date(
                              endUserOffcanvas.user.updatedAt,
                            ).toLocaleDateString("en-US", {
                              year: "numeric",
                              month: "short",
                              day: "numeric",
                            })}
                          </small>
                        </div>
                        <small className="text-muted">
                          {new Date(
                            endUserOffcanvas.user.updatedAt,
                          ).toLocaleTimeString("en-US", {
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                        </small>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </Offcanvas.Body>
      </Offcanvas>

      {/* Plan Details Popup */}
      {planDetailsModal.isOpen && (
        <>
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(0, 0, 0, 0.3)",
              zIndex: 998,
            }}
            onClick={() =>
              setPlanDetailsModal({
                isOpen: false,
                plans: null,
                position: { top: 0, left: 0 },
              })
            }
          />
          <div
            style={{
              position: "fixed",
              top: "166.141px",
              left: "1523px",
              backgroundColor: "white",
              border: "1px solid #ddd",
              borderRadius: "8px",
              boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
              zIndex: 999,
              width: "256px",
              height: "172px",
              overflowY: "auto",
              scrollbarWidth: "thin",
              scrollbarColor: "#888 #f1f1f1",
            }}
            className="thin-scrollbar"
          >
            <div className="p-2">
              {planDetailsModal.plans && planDetailsModal.plans.length > 0 ? (
                <div>
                  {planDetailsModal.plans.map((plan, index) => (
                    <div
                      key={index}
                      className="mb-2 p-2 border rounded bg-light"
                    >
                      <div className="row g-1">
                        <div className="col-6">
                          <small
                            className=" fw-medium d-block"
                            style={{ fontSize: "12px" }}
                          >
                            Plan Name
                          </small>
                          <span
                            className="text-muted d-block"
                            style={{ fontSize: "11px" }}
                          >
                            {plan.planName}
                          </span>
                        </div>

                        <div className="col-6">
                          <small
                            className=" fw-medium d-block"
                            style={{ fontSize: "12px" }}
                          >
                            Amount
                          </small>
                          <span
                            className="text-muted d-block"
                            style={{ fontSize: "11px" }}
                          >
                            {plan.currency} {plan.amount}
                          </span>
                        </div>
                        <div className="mb-1">
                          <small
                            className="text-muted d-block"
                            style={{ fontSize: "11px" }}
                          >
                            Stripe Product ID
                          </small>
                          <span
                            className="fw-medium"
                            style={{ fontSize: "11px" }}
                          >
                            {plan.stripeProductId}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-muted small">No plan details available</p>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default AlmataSplitView;
